import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

/**
 * 6-digit code input form. Calls `onSubmit(code)` and `onResend()` callbacks.
 */
export default function CodeVerifyForm({
  email,
  onSubmit,
  onResend,
  onBack,
  purpose = "login",
  submitLabel = "Verify",
  testidPrefix = "code",
}) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  // Start with a full cooldown so the button is disabled immediately after
  // the initial code send — user has to wait 60s before requesting another.
  const [cooldown, setCooldown] = useState(60);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      toast.error("Enter the 6-digit code from your email");
      return;
    }
    setLoading(true);
    try {
      await onSubmit(code);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    try {
      await onResend();
      toast.success("Code re-sent");
      setCooldown(60);
    } catch (err) {
      const d = err.response?.data?.detail;
      // Backend returns 429 with a Retry-After header when rate-limited.
      const retryAfter = Number(
        err.response?.headers?.["x-retry-after-seconds"] ||
        err.response?.headers?.["retry-after"] ||
        0
      );
      if (retryAfter > 0) setCooldown(retryAfter);
      toast.error(typeof d === "string" ? d : "Could not resend");
    } finally {
      setResending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={`${testidPrefix}-form`}>
      <div className="text-[11px] tracking-[0.4em] uppercase text-[#279153] mb-2">Two-step verification</div>
      <h1 className="font-serif-display text-3xl text-white mb-3">Check your email</h1>
      <p className="text-sm text-[#95A89C] mb-6">
        We sent a 6-digit code to <span className="text-white">{email}</span>. It expires in 10 minutes.
      </p>

      <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Verification code</label>
      <input
        ref={inputRef}
        data-testid={`${testidPrefix}-input`}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        className="w-full px-4 py-4 bg-[#050605] border border-[#1A2B20] text-white text-2xl tracking-[0.5em] text-center focus:border-[#279153] focus:outline-none mb-6 font-mono"
      />

      <button
        type="submit"
        data-testid={`${testidPrefix}-submit`}
        disabled={loading || code.length !== 6}
        className="w-full px-6 py-4 bg-[#1E7040] hover:bg-[#165932] text-white text-xs tracking-[0.3em] uppercase disabled:opacity-50"
      >
        {loading ? "…" : submitLabel}
      </button>

      <div className="mt-6 flex items-center justify-between text-xs">
        <button
          type="button"
          data-testid={`${testidPrefix}-back`}
          onClick={onBack}
          className="inline-flex items-center gap-2 text-[#95A89C] hover:text-white tracking-[0.2em] uppercase"
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <button
          type="button"
          data-testid={`${testidPrefix}-resend`}
          onClick={resend}
          disabled={resending || cooldown > 0}
          className="text-[#279153] hover:underline tracking-[0.2em] uppercase disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
          title={cooldown > 0 ? `Wait ${cooldown}s before requesting a new code` : undefined}
        >
          {resending ? "Sending…" : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </button>
      </div>
    </form>
  );
}
